import { localToday } from '../lib/flightFilters';
import { useEffect, useRef, useState } from 'react';
import { Logo } from '../components/Logo';

import { TRACKED_REG } from '../lib/flightTrack';
const DRAFT_KEY = 'ajm-instructor-draft-v1';

const CATS: { key: string; label: string }[] = [
  { key: 'SELand', label: 'SEL' },
  { key: 'Instrument', label: 'Instrument' },
  { key: 'MELand', label: 'MEL' },
  { key: 'SESea', label: 'SES' },
  { key: 'MESea', label: 'MES' },
  { key: 'RotorWing', label: 'Rotor' },
];
const LEVELS = ['Student', 'Private', 'Commercial', 'ATP', 'Instructor'] as const;
type Level = (typeof LEVELS)[number];

const CLAIM_Qs = [
  'Have you ever had an aircraft accident/incident or been penalized for a FAR violation?',
  'Has any insurer ever cancelled, declined, or refused to renew insurance on your behalf?',
  'Have you ever been convicted of driving under the influence, or of reckless driving?',
  "Has your driver's license ever been suspended or revoked?",
  'Have you ever been convicted of, or are you under indictment in, a legal action involving drugs or narcotics?',
];

const ENDPOINT = `${import.meta.env.BASE_URL}api/instructor-pef`;

function loadDraft(): { f?: unknown; ratings?: unknown; claims?: unknown } | null {
  try {
    return JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null');
  } catch {
    return null;
  }
}

export function InstructorIntake() {
  const today = localToday();
  const usDate = (iso: string) => (iso ? iso.slice(5, 7) + '/' + iso.slice(8, 10) + '/' + iso.slice(0, 4) : '');
  const draft = useRef(loadDraft()).current;

  const [f, setF] = useState<any>(() => draft?.f ?? ({
    pilotName: '', dob: '', address: '', cityStateZip: '', occupation: '', employer: '',
    certNumber: '', aopa: '',
    email: '', phoneC: '', phoneH: '', phoneW: '',
    emgName: '', emgRelationship: '', emgPhone: '', emgEmail: '',
    typeRatings: '',
    medicalDate: '', medicalClass: '', flightReviewDate: '', ipcDate: '',
    medicalWaiver: '', medicalWaiverNotes: '', medicalLimits: '', medicalLimitsNotes: '',
    hours: {
      totalTime: '', pic: '', sic: '', multiEngine: '', retractable: '', tailwheel: '',
      instrument: '', instrumentActual: '', instrumentSimulated: '', cfiDualGiven: '',
      seaplane: '', turbine: '', rotorWing: '', last90: '', last12: '',
    } as Record<string, string>,
    c172Total: '', c172Last90: '', c172Last12: '',
    signatureName: '', attest: false,
  }));
  const [ratings, setRatings] = useState<Record<Level, string[]>>(
    () => (draft?.ratings as Record<Level, string[]>) ?? { Student: [], Private: [], Commercial: [], ATP: [], Instructor: [] }
  );
  const [claims, setClaims] = useState<{ answer: string; explain: string }[]>(
    () => (draft?.claims as { answer: string; explain: string }[]) ?? CLAIM_Qs.map(() => ({ answer: '', explain: '' }))
  );

  // This public page is always light, regardless of the device/portal theme —
  // the pre-paint script in index.html skips /instructor; this covers in-app
  // navigation and any later applyTheme() call.
  useEffect(() => {
    document.documentElement.classList.remove('dark');
  }, []);

  // Auto-save progress to this device so a CFI can close and come back to finish.
  useEffect(() => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ f, ratings, claims }));
    } catch {
      /* storage full / disabled — non-fatal */
    }
  }, [f, ratings, claims]);
  const [showMoreHours, setShowMoreHours] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const upd = (k: string, v: string | boolean) => setF((p: any) => ({ ...p, [k]: v }));
  const updHours = (k: string, v: string) => setF((p: any) => ({ ...p, hours: { ...p.hours, [k]: v } }));
  const toggleRating = (level: Level, cat: string) =>
    setRatings((p) => ({ ...p, [level]: p[level].includes(cat) ? p[level].filter((c) => c !== cat) : [...p[level], cat] }));

  const canSubmit =
    f.pilotName.trim() && f.email.trim() && f.certNumber.trim() && f.signatureName.trim() && f.attest && !busy;

  async function submit() {
    setErr(null);
    if (!canSubmit) {
      setErr('Please fill your name, email, certificate number, type your signature, and check the box.');
      return;
    }
    setBusy(true);
    const anyRatings = Object.fromEntries(Object.entries(ratings).filter(([, v]) => v.length));
    const data = {
      pilotName: f.pilotName, dob: usDate(f.dob), address: f.address, cityStateZip: f.cityStateZip,
      occupation: f.occupation, employer: f.employer, certNumber: f.certNumber, aopa: f.aopa,
      email: f.email, phoneC: f.phoneC, phoneH: f.phoneH, phoneW: f.phoneW,
      emgName: f.emgName, emgRelationship: f.emgRelationship, emgPhone: f.emgPhone, emgEmail: f.emgEmail,
      ratings: anyRatings, typeRatings: f.typeRatings,
      medicalDate: usDate(f.medicalDate), medicalClass: f.medicalClass,
      flightReviewDate: usDate(f.flightReviewDate), ipcDate: usDate(f.ipcDate),
      medicalWaiver: f.medicalWaiver, medicalWaiverNotes: f.medicalWaiverNotes,
      medicalLimits: f.medicalLimits, medicalLimitsNotes: f.medicalLimitsNotes,
      hours: f.hours,
      makeModel: { name: `Cessna 172 (${TRACKED_REG})`, total: f.c172Total, last90: f.c172Last90, last12: f.c172Last12 },
      claims,
      signatureName: f.signatureName, signDate: usDate(today),
    };
    try {
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data }),
      });
      const j = await res.json();
      if (!j.ok) throw new Error(j.message || 'Something went wrong.');
      try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
      setDone(true);
      window.scrollTo(0, 0);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not submit. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="min-h-screen bg-sky-50 px-5 py-16">
        <div className="mx-auto max-w-md rounded-2xl bg-white p-8 text-center shadow-sm">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-3xl">✓</div>
          <h1 className="font-display text-2xl font-bold text-navy-900">You're all set.</h1>
          <p className="mt-3 text-sm text-ink-muted">
            Thanks, {f.pilotName.split(' ')[0] || 'there'}. Your Named Pilot Form has been sent to AJM Aviation.
            We'll get you added to {TRACKED_REG}&rsquo;s policy through Wings — you'll hear from them shortly. Once you're on the
            policy, you're cleared to instruct in the airplane.
          </p>
          <p className="mt-4 text-xs text-ink-subtle">You can close this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sky-50">
      {/* header */}
      <div className="border-b border-slate-200 bg-white px-5 pb-6 pt-7 md:px-0 md:pb-8 md:pt-10">
        <div className="mx-auto max-w-md md:max-w-3xl md:px-8 lg:max-w-6xl md:flex md:items-end md:justify-between md:gap-6">
          <div>
            <Logo className="text-3xl md:text-4xl" />
            <h1 className="mt-5 font-display text-2xl font-bold leading-tight text-navy-900 md:mt-7 md:text-4xl">Named Pilot Form</h1>
            <p className="mt-0.5 text-sm font-semibold text-navy-700 md:mt-1 md:text-base">{TRACKED_REG} · 1961 Cessna 172C</p>
          </div>
          <p className="mt-2 text-xs text-ink-subtle md:mb-1 md:mt-0 md:text-sm">Progress saves automatically</p>
        </div>
      </div>

      <div className="mx-auto max-w-md px-5 py-6 md:max-w-3xl md:px-8 md:py-10 lg:grid lg:max-w-6xl lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-10">
        <aside className="hidden lg:block">
          <nav className="sticky top-8">
            <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-ink-subtle">Sections</div>
            <ul className="space-y-1 text-sm">
              {SECTIONS.map((t) => (
                <li key={t}>
                  <a href={`#${slug(t)}`} className="block rounded-lg px-3 py-1.5 text-navy-800 hover:bg-white hover:text-navy-900">{t}</a>
                </li>
              ))}
            </ul>
            <p className="mt-6 px-3 text-xs leading-relaxed text-ink-subtle">
              This fills the insurance form that adds you to {TRACKED_REG}&rsquo;s policy as an approved instructor.
            </p>
          </nav>
        </aside>

        <div className="min-w-0 space-y-5 md:space-y-6">
        <Section title="About you">
          <Grid cols={3}>
            <Field label="Full name" value={f.pilotName} onChange={(v) => upd('pilotName', v)} required wide />
            <Field label="Date of birth" type="date" value={f.dob} onChange={(v) => upd('dob', v)} />
            <Field label="Certificate #" value={f.certNumber} onChange={(v) => upd('certNumber', v)} required />
          </Grid>
          <Grid cols={2}>
            <Field label="Street address" value={f.address} onChange={(v) => upd('address', v)} wide />
            <Field label="City, State ZIP" value={f.cityStateZip} onChange={(v) => upd('cityStateZip', v)} wide />
          </Grid>
          <Grid cols={3}>
            <Field label="Occupation" value={f.occupation} onChange={(v) => upd('occupation', v)} />
            <Field label="Employer" value={f.employer} onChange={(v) => upd('employer', v)} />
            <Field label="AOPA # (optional)" value={f.aopa} onChange={(v) => upd('aopa', v)} wide />
          </Grid>
        </Section>

        <Section title="Contact">
          <Grid cols={3}>
            <Field label="Email" type="email" value={f.email} onChange={(v) => upd('email', v)} required wide />
            <Field label="Cell phone" type="tel" value={f.phoneC} onChange={(v) => upd('phoneC', v)} />
            <Field label="Home phone" type="tel" value={f.phoneH} onChange={(v) => upd('phoneH', v)} />
          </Grid>
          <div className="mt-1 text-xs font-semibold uppercase tracking-wide text-ink-subtle md:pt-2">Emergency contact</div>
          <Grid cols={3}>
            <Field label="Name" value={f.emgName} onChange={(v) => upd('emgName', v)} wide />
            <Field label="Relationship" value={f.emgRelationship} onChange={(v) => upd('emgRelationship', v)} />
            <Field label="Phone" type="tel" value={f.emgPhone} onChange={(v) => upd('emgPhone', v)} />
          </Grid>
        </Section>

        <Section title="Certificates & ratings" hint="Tap the ratings you hold at each level.">
          <div className="space-y-3 md:space-y-2.5">
            {LEVELS.map((lvl) => (
              <div key={lvl} className="md:flex md:items-center md:gap-4">
                <div className="mb-1.5 text-sm font-semibold text-navy-900 md:mb-0 md:w-28 md:shrink-0">{lvl}</div>
                <div className="flex flex-wrap gap-1.5">
                  {CATS.map((c) => {
                    const on = ratings[lvl].includes(c.key);
                    // Student only has SEL/Rotor cells on the form
                    if (lvl === 'Student' && !['SELand', 'RotorWing'].includes(c.key)) return null;
                    return (
                      <button
                        key={c.key}
                        type="button"
                        onClick={() => toggleRating(lvl, c.key)}
                        className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${
                          on ? 'bg-brand text-white' : 'bg-white text-navy-800 ring-1 ring-inset ring-slate-200'
                        }`}
                      >
                        {c.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <Field label="Type ratings & LOAs (if any)" value={f.typeRatings} onChange={(v) => upd('typeRatings', v)} />
        </Section>

        <Section title="Medical & currency">
          <Grid cols={4}>
            <Field label="Medical date" type="date" value={f.medicalDate} onChange={(v) => upd('medicalDate', v)} />
            <div>
              <label className="label">Class</label>
              <select className="input" value={f.medicalClass} onChange={(e) => upd('medicalClass', e.target.value)}>
                <option value="">—</option>
                {['1st', '2nd', '3rd', 'BasicMed'].map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
            <Field label="Flight review date" type="date" value={f.flightReviewDate} onChange={(v) => upd('flightReviewDate', v)} />
            <Field label="Last IPC (if any)" type="date" value={f.ipcDate} onChange={(v) => upd('ipcDate', v)} />
          </Grid>
          <div className="space-y-3 md:grid md:grid-cols-2 md:gap-4 md:space-y-0">
          <div className="space-y-3">
          <YesNo label="Flying under a medical waiver?" value={f.medicalWaiver} onChange={(v) => upd('medicalWaiver', v)} />
          {f.medicalWaiver === 'yes' && (
            <Field label="Explain" value={f.medicalWaiverNotes} onChange={(v) => upd('medicalWaiverNotes', v)} />
          )}
          </div>
          <div className="space-y-3">
          <YesNo label="Any limitations on your medical?" value={f.medicalLimits} onChange={(v) => upd('medicalLimits', v)} />
          {f.medicalLimits === 'yes' && (
            <Field label="Explain" value={f.medicalLimitsNotes} onChange={(v) => upd('medicalLimitsNotes', v)} />
          )}
          </div>
          </div>
        </Section>

        <Section title="Flight hours" hint="Numbers only (e.g. 1240 or 525.5).">
          <Grid cols={4}>
            <Num label="Total time" value={f.hours.totalTime} onChange={(v) => updHours('totalTime', v)} />
            <Num label="PIC" value={f.hours.pic} onChange={(v) => updHours('pic', v)} />
            <Num label="Instrument (actual)" value={f.hours.instrumentActual} onChange={(v) => updHours('instrumentActual', v)} />
            <Num label="Instrument (sim)" value={f.hours.instrumentSimulated} onChange={(v) => updHours('instrumentSimulated', v)} />
            <Num label="CFI dual given" value={f.hours.cfiDualGiven} onChange={(v) => updHours('cfiDualGiven', v)} />
            <Num label="Multi-engine" value={f.hours.multiEngine} onChange={(v) => updHours('multiEngine', v)} />
            <Num label="Last 90 days" value={f.hours.last90} onChange={(v) => updHours('last90', v)} />
            <Num label="Last 12 months" value={f.hours.last12} onChange={(v) => updHours('last12', v)} />
          </Grid>
          <button type="button" className="text-xs font-medium text-navy-700 underline" onClick={() => setShowMoreHours((s) => !s)}>
            {showMoreHours ? 'Hide' : 'More categories (SIC, tailwheel, seaplane, turbine…)'}
          </button>
          {showMoreHours && (
            <div className="mt-2">
              <Grid cols={3}>
                <Num label="SIC" value={f.hours.sic} onChange={(v) => updHours('sic', v)} />
                <Num label="Retractable" value={f.hours.retractable} onChange={(v) => updHours('retractable', v)} />
                <Num label="Tailwheel" value={f.hours.tailwheel} onChange={(v) => updHours('tailwheel', v)} />
                <Num label="Total instrument" value={f.hours.instrument} onChange={(v) => updHours('instrument', v)} />
                <Num label="Seaplane" value={f.hours.seaplane} onChange={(v) => updHours('seaplane', v)} />
                <Num label="Turbine" value={f.hours.turbine} onChange={(v) => updHours('turbine', v)} />
              </Grid>
            </div>
          )}
        </Section>

        <Section title="Time in the Cessna 172" hint={`Your hours in ${TRACKED_REG}'s make & model.`}>
          <Grid cols={3}>
            <Num label="Total in 172" value={f.c172Total} onChange={(v) => upd('c172Total', v)} />
            <Num label="Last 90 days" value={f.c172Last90} onChange={(v) => upd('c172Last90', v)} />
            <Num label="Last 12 months" value={f.c172Last12} onChange={(v) => upd('c172Last12', v)} />
          </Grid>
        </Section>

        <Section title="Accident & claims history" hint='Answer honestly — explain any "Yes" fully.'>
          <div className="space-y-4 md:space-y-0 md:divide-y md:divide-slate-100">
            {CLAIM_Qs.map((q, i) => (
              <div key={i} className="md:py-3.5 md:first:pt-1 md:last:pb-0">
                <div className="md:flex md:items-center md:justify-between md:gap-6">
                <div className="text-sm text-navy-900">{q}</div>
                <div className="mt-1.5 inline-flex shrink-0 overflow-hidden rounded-lg ring-1 ring-inset ring-slate-200 md:mt-0">
                  {(['no', 'yes'] as const).map((a) => (
                    <button
                      key={a}
                      type="button"
                      onClick={() => setClaims((p) => p.map((c, ci) => (ci === i ? { ...c, answer: a } : c)))}
                      className={`px-4 py-1.5 text-sm font-medium capitalize transition ${
                        claims[i].answer === a ? 'bg-brand text-white' : 'bg-white text-ink-muted'
                      }`}
                    >
                      {a}
                    </button>
                  ))}
                </div>
                </div>
                {claims[i].answer === 'yes' && (
                  <textarea
                    className="input-textarea mt-2 text-sm"
                    placeholder="Explain fully"
                    value={claims[i].explain}
                    onChange={(e) => setClaims((p) => p.map((c, ci) => (ci === i ? { ...c, explain: e.target.value } : c)))}
                  />
                )}
              </div>
            ))}
          </div>
        </Section>

        <Section title="Sign & submit">
          <label className="flex items-start gap-2 text-sm text-ink">
            <input type="checkbox" className="mt-1" checked={f.attest} onChange={(e) => upd('attest', e.target.checked)} />
            <span>I warrant that the answers given are true and complete to the best of my knowledge, and no material information has been withheld.</span>
          </label>
          <Field label="Type your full name as signature" value={f.signatureName} onChange={(v) => upd('signatureName', v)} required />
          <p className="text-xs text-ink-subtle">Dated {usDate(today)}. Your info is used to fill the insurance form and is emailed to AJM — not stored.</p>
          {err && <p className="text-sm text-red-700">{err}</p>}
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            className="btn-primary w-full py-3 disabled:opacity-50 md:w-auto md:px-10"
          >
            {busy ? 'Sending…' : 'Submit to AJM'}
          </button>
        </Section>

        <p className="pb-10 text-center text-xs text-ink-subtle">AJM Aviation Ventures, LLC · {TRACKED_REG} · Long Beach (KLGB)</p>
        </div>
      </div>
    </div>
  );
}

/* ---- little building blocks ---- */
const SECTIONS = [
  'About you', 'Contact', 'Certificates & ratings', 'Medical & currency', 'Flight hours',
  'Time in the Cessna 172', 'Accident & claims history', 'Sign & submit',
];
const slug = (t: string) => t.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <div id={slug(title)} className="scroll-mt-6 rounded-2xl bg-white p-5 shadow-sm md:p-7">
      <h2 className="font-display text-lg font-semibold text-navy-900 md:text-xl">{title}</h2>
      {hint && <p className="mb-3 mt-0.5 text-xs text-ink-muted">{hint}</p>}
      <div className={hint ? 'space-y-3' : 'mt-3 space-y-3'}>{children}</div>
    </div>
  );
}
// Two columns on phones, widening on iPad/desktop. `wide` fields span the full row on phones.
const GRID_COLS = { 2: 'md:grid-cols-2', 3: 'md:grid-cols-3', 4: 'md:grid-cols-4' } as const;
function Grid({ cols, children }: { cols: keyof typeof GRID_COLS; children: React.ReactNode }) {
  return <div className={`grid grid-cols-2 gap-3 md:gap-4 ${GRID_COLS[cols]}`}>{children}</div>;
}
function Field({ label, value, onChange, type = 'text', required, wide }: { label: string; value: string; onChange: (v: string) => void; type?: string; required?: boolean; wide?: boolean }) {
  return (
    <div className={wide ? 'col-span-2 md:col-span-1' : undefined}>
      <label className="label">{label}{required && <span className="text-red-500"> *</span>}</label>
      <input className="input" type={type} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
function Num({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="label">{label}</label>
      <input className="input" type="number" inputMode="decimal" step="0.1" value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
function YesNo({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="label">{label}</label>
      <div className="inline-flex overflow-hidden rounded-lg ring-1 ring-inset ring-slate-200">
        {(['no', 'yes'] as const).map((a) => (
          <button
            key={a}
            type="button"
            onClick={() => onChange(a)}
            className={`px-4 py-1.5 text-sm font-medium capitalize transition ${value === a ? 'bg-brand text-white' : 'bg-white text-ink-muted'}`}
          >
            {a}
          </button>
        ))}
      </div>
    </div>
  );
}
